import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FEED_CHANNELS,
  FEED_CONDITIONS,
  FEED_LEFT_OUT,
  FEEDS_DEFAULTS,
  type FeedChannel,
  type FeedIssueSummaryDto,
  type FeedsConfigSetting,
} from '@ecom/contracts';
import { SelectField, ToggleField } from '@/components/fields';
import { CheckIcon } from '@/components/icons';
import { AnnotatedSection, Page, SaveBar } from '@/components/page';
import { Alert, Card, CopyButton, Pill, Skeleton, Switch, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { age, count } from '@/lib/format';
import { storefrontOrigin } from '@/lib/storefront-url';
import { useAuthStore } from '@/stores/auth.store';
import { useFeedsStore } from '@/stores/feeds.store';

/** Where an issue is fixed: the product, or for a Google category, the categories. */
function fixLink(issue: FeedIssueSummaryDto['issue'], id: string): string {
  return issue === 'no-google-category' ? '/categories' : `/products/${id}`;
}

/**
 * Marketing → Product feeds (ADR-096): the addresses Google Merchant Center,
 * Meta, TikTok and Snapchat read the catalogue from, the store's choices for
 * them, and the check the API runs before a platform does.
 */
export function FeedsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const owner = useAuthStore((state) => state.user?.role === 'OWNER');
  const { status, loading, saving, error, load, save } = useFeedsStore();
  const stored = useMemo(() => status?.config ?? FEEDS_DEFAULTS, [status]);
  const [draft, setDraft] = useState<FeedsConfigSetting>(stored);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => setDraft(stored), [stored]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  const origin = storefrontOrigin() ?? '';
  const leftOut = status?.issues.filter((entry) => FEED_LEFT_OUT.includes(entry.issue)) ?? [];
  const warnings = status?.issues.filter((entry) => !FEED_LEFT_OUT.includes(entry.issue)) ?? [];
  const setChannel = (channel: FeedChannel, on: boolean) => setDraft((current) => ({ ...current, channels: { ...current.channels, [channel]: on } }));

  return (
    <Page title={t('feedsPage')} subtitle={t('feedsPageHint')}>
      {error ? <Alert>{error}</Alert> : null}
      {loading && !status ? (
        <Skeleton className="h-96" />
      ) : status ? (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-ink-muted">
            {t('feedsSummary', { rows: count(status.rows, locale), products: count(status.products, locale) })}
          </p>

          <div className="grid gap-4 lg:grid-cols-2">
            {FEED_CHANNELS.map((channel) => {
              const on = draft.channels[channel];
              const read = status.lastRead[channel];
              return (
                <Card key={channel} className="flex flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold text-ink">{t(`feedChannel_${channel}`)}</h2>
                      <p className="mt-0.5 text-xs text-ink-muted">{t(`feedChannelHint_${channel}`)}</p>
                    </div>
                    {owner ? (
                      <Switch checked={on} onChange={(next) => setChannel(channel, next)} label={t(`feedChannel_${channel}`)} />
                    ) : (
                      <Pill tone={on ? 'success' : 'neutral'}>{on ? t('feedOn') : t('feedOff')}</Pill>
                    )}
                  </div>
                  {on ? (
                    <>
                      {(['ar', 'en'] as const).map((language) => {
                        const url = `${origin}/feeds/${channel}-${language}.xml`;
                        return (
                          <div key={language} className="flex items-center gap-2">
                            <span className="w-14 shrink-0 text-xs text-ink-muted">{t(`feedLanguage_${language}`)}</span>
                            <code dir="ltr" className="min-w-0 flex-1 break-all rounded bg-surface-sunken px-2 py-1 text-start font-mono text-xs text-ink">
                              {url}
                            </code>
                            <CopyButton value={url} label={t('copy')} copiedLabel={t('copied')} />
                          </div>
                        );
                      })}
                      <p className="text-xs text-ink-muted">
                        {stored.channels[channel]
                          ? read
                            ? t('feedLastRead', { when: age(read, locale) })
                            : t('feedNeverRead')
                          : t('feedSaveFirst')}
                      </p>
                    </>
                  ) : null}
                </Card>
              );
            })}
          </div>

          {owner ? (
            <Card className="flex flex-col gap-6 p-5">
              <AnnotatedSection title={t('feedsSettings')} description={t('feedsSettingsHint')}>
                <div className="flex flex-col gap-4">
                  <ToggleField
                    label={t('feedsOutOfStock')}
                    hint={t('feedsOutOfStockHint')}
                    checked={draft.includeOutOfStock}
                    onChange={(includeOutOfStock) => setDraft((current) => ({ ...current, includeOutOfStock }))}
                  />
                  <ToggleField
                    label={t('feedsBrandFallback')}
                    hint={t('feedsBrandFallbackHint')}
                    checked={draft.brandFallback}
                    onChange={(brandFallback) => setDraft((current) => ({ ...current, brandFallback }))}
                  />
                  <SelectField
                    label={t('feedsCondition')}
                    hint={t('feedsConditionHint')}
                    value={draft.condition}
                    options={FEED_CONDITIONS.map((condition) => ({ value: condition, label: t(`feedCondition_${condition}`) }))}
                    onChange={(condition) => setDraft((current) => ({ ...current, condition: condition as FeedsConfigSetting['condition'] }))}
                  />
                </div>
              </AnnotatedSection>
              <SaveBar saving={saving} dirty={dirty} onSave={() => void save(draft)} onDiscard={() => setDraft(stored)} />
            </Card>
          ) : null}

          <IssueGroup title={t('feedsLeftOut')} hint={t('feedsLeftOutHint')} issues={leftOut} tone="danger" />
          <IssueGroup title={t('feedsWarnings')} hint={t('feedsWarningsHint')} issues={warnings} tone="warning" />
        </div>
      ) : null}
    </Page>
  );
}

/** A group of issues: each a count, what it means, and the first products to open. */
function IssueGroup({ title, hint, issues, tone }: { title: string; hint: string; issues: FeedIssueSummaryDto[]; tone: 'warning' | 'danger' }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  return (
    <Card className="p-5">
      <h2 className="text-base font-semibold text-ink">{title}</h2>
      <p className="mt-0.5 text-sm text-ink-muted">{hint}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {issues.map((entry) => (
          <div key={entry.issue} className="rounded-card border border-line p-4">
            <div className="flex items-center gap-2">
              <span data-numeric className={cx('text-xl font-bold', entry.count === 0 ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-warning')}>
                {entry.count === 0 ? <CheckIcon className="size-5" /> : count(entry.count, locale)}
              </span>
              <span className="text-sm font-medium text-ink">{t(`feedIssue_${entry.issue}`)}</span>
            </div>
            <p className="mt-1 text-xs text-ink-muted">{t(`feedIssueHint_${entry.issue}`)}</p>
            {entry.examples.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1">
                {entry.examples.map((example) => (
                  <li key={example.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate text-ink">{example.name}</span>
                    <Link to={fixLink(entry.issue, example.id)} className="shrink-0 text-xs font-medium text-brand-700 hover:underline">
                      {t('seoFix')}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
